import { useState } from 'react';
import {
  ArrowLeft, Edit, ArrowRightLeft, Trophy, XCircle, Plus,
  Building2, DollarSign, Shield, Calendar, Hash, Users,
} from 'lucide-react';
import { useDeal, useUpdateDeal, useUpdateDealStage } from '../hooks/useDeals';
import { usePipelines } from '../hooks/usePipelines';
import { useNotes, useCreateNote } from '../hooks/useNotes';
import { useActivities } from '../hooks/useActivities';
import { Badge } from '../components/shared/Badge';
import { StageProgressBar } from '../components/shared/StageProgressBar';
import { NoteEditor } from '../components/shared/NoteEditor';
import { RecordTimeline, type TimelineItem } from '../components/shared/RecordTimeline';
import { EmptyState } from '../components/shared/EmptyState';

interface DealDetailViewProps {
  dealId: string;
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

function formatCurrency(value?: number): string {
  if (value == null) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString();
}

const statusColors: Record<string, string> = {
  open: 'blue',
  won: 'green',
  lost: 'red',
};

export function DealDetailView({ dealId, onNavigate }: DealDetailViewProps) {
  const { data: deal, isLoading, error } = useDeal(dealId === 'new' ? null : dealId);
  const { data: pipelines } = usePipelines();
  const { data: notesData } = useNotes('deal', dealId === 'new' ? null : dealId);
  const { data: activitiesData } = useActivities('deal', dealId === 'new' ? null : dealId);
  const createNote = useCreateNote();
  const updateDeal = useUpdateDeal();
  const updateStage = useUpdateDealStage();
  const [showMoveStage, setShowMoveStage] = useState(false);

  if (dealId === 'new') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('deals')} className="btn-ghost rounded-full p-1.5">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h1 className="text-xl font-bold text-slate-900">New Deal</h1>
          </div>
        </div>
        <div className="p-6">
          <div className="card max-w-2xl p-6">
            <p className="text-sm text-slate-500">
              Deal creation form will be implemented here. Use the API to create deals via POST /api/deals.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-surface-200" />
          <div className="h-32 rounded-lg bg-surface-200" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-64 rounded-lg bg-surface-200" />
            <div className="h-64 rounded-lg bg-surface-200" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="flex-1 p-6">
        <EmptyState
          icon={DollarSign}
          title="Deal not found"
          description="This deal may have been deleted or you may not have access."
          actionLabel="Back to Deals"
          onAction={() => onNavigate('deals')}
        />
      </div>
    );
  }

  const pipeline = pipelines?.find((p) => p.id === deal.pipelineId);
  const stages = pipeline?.stages ?? [];

  const timelineItems: TimelineItem[] = (activitiesData?.activities ?? []).map((a) => ({
    id: a.id,
    type: a.type,
    description: a.description,
    userName: a.userName,
    createdAt: a.createdAt,
  }));

  function handleAddNote(content: string) {
    createNote.mutate({ content, entityType: 'deal', entityId: dealId });
  }

  function handleMarkWon() {
    updateDeal.mutate({ id: dealId, data: { status: 'won' } });
  }

  function handleMarkLost() {
    updateDeal.mutate({ id: dealId, data: { status: 'lost' } });
  }

  function handleMoveToStage(stageId: string) {
    updateStage.mutate({ id: dealId, stageId });
    setShowMoveStage(false);
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate('deals')} className="btn-ghost rounded-full p-1.5">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">{deal.name}</h1>
              {deal.stageName && (
                <Badge color="blue">{deal.stageName}</Badge>
              )}
              <Badge color={statusColors[deal.status] ?? 'gray'}>{deal.status}</Badge>
              {deal.dealType && <Badge color="purple">{deal.dealType}</Badge>}
            </div>
            {deal.organizationName && (
              <button
                onClick={() => deal.organizationId && onNavigate('org-detail', { orgId: deal.organizationId })}
                className="mt-0.5 text-sm text-brand-600 hover:underline"
              >
                {deal.organizationName}
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button className="btn-secondary btn-sm">
            <Edit className="h-3.5 w-3.5" />
            Edit
          </button>
          <button
            className="btn-secondary btn-sm"
            onClick={() => setShowMoveStage(!showMoveStage)}
          >
            <ArrowRightLeft className="h-3.5 w-3.5" />
            Move Stage
          </button>
          {deal.status === 'open' && (
            <>
              <button className="btn-sm bg-green-600 text-white hover:bg-green-700" onClick={handleMarkWon}>
                <Trophy className="h-3.5 w-3.5" />
                Won
              </button>
              <button className="btn-sm bg-red-600 text-white hover:bg-red-700" onClick={handleMarkLost}>
                <XCircle className="h-3.5 w-3.5" />
                Lost
              </button>
            </>
          )}
        </div>
      </div>

      {/* Move Stage Selector */}
      {showMoveStage && stages.length > 0 && (
        <div className="border-b border-surface-200 bg-surface-50 px-6 py-3">
          <p className="mb-2 text-xs font-medium text-slate-500">Move to stage:</p>
          <div className="flex flex-wrap gap-2">
            {stages.map((stage) => (
              <button
                key={stage.id}
                onClick={() => handleMoveToStage(stage.id)}
                className={`btn-sm rounded-full border ${
                  stage.id === deal.stageId
                    ? 'border-brand-300 bg-brand-50 text-brand-700'
                    : 'border-surface-200 bg-white text-slate-600 hover:border-brand-200'
                }`}
              >
                {stage.color && (
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: stage.color }} />
                )}
                {stage.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stage Progress */}
      {stages.length > 0 && (
        <div className="border-b border-surface-200 bg-white px-6 py-4">
          <StageProgressBar stages={stages} currentStageId={deal.stageId} />
        </div>
      )}

      {/* Content */}
      <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-3">
        {/* Left Column */}
        <div className="space-y-4 lg:col-span-1">
          {/* Deal Info */}
          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Deal Details</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="label flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Premium</span>
                <span className="text-sm font-medium text-slate-900">{formatCurrency(deal.premium)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="label flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Commission</span>
                <span className="text-sm text-slate-600">{formatCurrency(deal.commission)}</span>
              </div>
              {deal.carrier && (
                <div className="flex items-center justify-between">
                  <span className="label flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Carrier</span>
                  <span className="text-sm text-slate-600">{deal.carrier}</span>
                </div>
              )}
              {deal.policyNumber && (
                <div className="flex items-center justify-between">
                  <span className="label flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" /> Policy #</span>
                  <span className="text-sm text-slate-600">{deal.policyNumber}</span>
                </div>
              )}
              {deal.effectiveDate && (
                <div className="flex items-center justify-between">
                  <span className="label flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Effective</span>
                  <span className="text-sm text-slate-600">{formatDate(deal.effectiveDate)}</span>
                </div>
              )}
              {deal.expirationDate && (
                <div className="flex items-center justify-between">
                  <span className="label flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Expiration</span>
                  <span className="text-sm text-slate-600">{formatDate(deal.expirationDate)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Coverage Types */}
          {deal.coverageTypes && deal.coverageTypes.length > 0 && (
            <div className="card p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Coverage Types</h3>
              <div className="flex flex-wrap gap-1.5">
                {deal.coverageTypes.map((ct) => (
                  <Badge key={ct} color="indigo" size="md">{ct}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Linked Contacts */}
          <div className="card p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Contacts</h3>
              <button className="btn-ghost btn-sm p-1"><Plus className="h-3.5 w-3.5" /></button>
            </div>
            {deal.contacts && deal.contacts.length > 0 ? (
              <div className="space-y-2">
                {deal.contacts.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => onNavigate('contact-detail', { contactId: c.id })}
                    className="flex w-full items-center gap-2 rounded-md p-1.5 text-left hover:bg-surface-50"
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-100 text-2xs font-semibold text-brand-700">
                      {c.firstName?.[0]}{c.lastName?.[0]}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700">{c.firstName} {c.lastName}</div>
                      {c.email && <div className="text-2xs text-slate-400">{c.email}</div>}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No linked contacts.</p>
            )}
          </div>

          {/* Organization */}
          {deal.organizationName && (
            <div className="card p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Organization</h3>
              <button
                onClick={() => deal.organizationId && onNavigate('org-detail', { orgId: deal.organizationId })}
                className="flex items-center gap-2 text-sm text-brand-600 hover:underline"
              >
                <Building2 className="h-4 w-4" />
                {deal.organizationName}
              </button>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-4 lg:col-span-2">
          {/* Notes */}
          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Notes</h3>
            <NoteEditor onSubmit={handleAddNote} isSubmitting={createNote.isPending} />
            {notesData && notesData.notes.length > 0 && (
              <div className="mt-4 divide-y divide-surface-100">
                {notesData.notes.map((note) => (
                  <div key={note.id} className="py-3">
                    <p className="whitespace-pre-wrap text-sm text-slate-600">{note.content}</p>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                      {note.authorName && <span>{note.authorName}</span>}
                      <span>{new Date(note.createdAt).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Activity Timeline</h3>
            <RecordTimeline activities={timelineItems} />
          </div>
        </div>
      </div>
    </div>
  );
}
